import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./ViewRemedial.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { FaCheck } from "react-icons/fa";

function ViewRemedialResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);

  // Auth / user context (matching ListAssessment pattern)
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const slug = String(user?.role?.slug || "").toLowerCase();
  const navigateUser = slug === "staff" ? "/user/assessmentList" : "/user/assessment";
  const user_id = user?.id || "";

  // Coming from ListAssessment via navigate("/user/viewAssessment", { state: { ID, data, mode } })
  const assessmentId = location?.state?.ID;
  const studentId = user_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch assessment result
  useEffect(() => {
    if (assessmentId && studentId) {
      fetchAssessmentResult();
    } else {
      setError("Missing assessment or student information");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, studentId]);

  const fetchAssessmentResult = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        school_id: school_id,
        id: assessmentId,
        student_id: studentId,
        // student_id: 2946,

        // school_id: 2,
        // assessment_id: 10,
        // student_id: 2946,
      };

      const response = await AxiosInstance.post(
        `/book/student/resultRemedialAssessment`,
        payload,
      );

      if (response?.data?.status) {
        setData(response.data.data);
      } else {
        setError(response?.data?.message || "Failed to fetch assessment");
      }
    } catch (err) {
      console.error("Error fetching assessment result:", err);
      setError(
        err?.response?.data?.message || err?.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(navigateUser, { state: { activeTab: 1 } });
  };

  // Map letter (a/b/c/d) to option text
  const getOptionText = (question, letter) => {
    if (!letter) return "";
    const key = `option_${letter.toLowerCase()}`;
    return question[key];
  };

  const optionLetters = ["a", "b", "c", "d", "e", "f"];

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.spinner}></div>
        <p>{translations?.labels?.loading || "Loading assessment..."}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorWrapper}>
        <p>⚠ {error}</p>
        <button onClick={handleBack} className={styles.backButton}>
          {translations?.labels?.back || "Back"}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { remedial: assessment, result, questions, summary } = data;

  const percentage = parseFloat(result?.percentage || 0);
  const isPassed = (result?.status || "").toString().toLowerCase() === "pass";

  return (
    <div className={styles.container}>
      {/* Summary Bar */}
      <div className={styles.summaryBar}>
        <div className={styles.summaryItem}>
          <div className={styles.scoreValue}>{percentage.toFixed(0)}%</div>
          <div className={styles.scoreLabel}>
            {translations?.labels?.totalScore || "Total Score"}
          </div>
        </div>

        <div className={styles.divider}></div>

        <div className={styles.summaryItem}>
          <div
            className={`${styles.statusValue} ${isPassed ? styles.passed : styles.failed
              }`}
          >
            {isPassed
              ? translations?.labels?.pass || "Pass"
              : translations?.labels?.fail || "Fail"}
          </div>
          <div className={styles.scoreLabel}>
            {translations?.labels?.status || "Status"}
          </div>
        </div>

        <div className={styles.divider}></div>

        <div className={`${styles.statBox} ${styles.correctBox}`}>
          <div className={styles.statValue}>
            <span className={styles.checkIcon}>✓</span>
            {summary?.correct_answers ?? 0}
          </div>
          <div className={styles.statLabel}>
            {translations?.labels?.correctAnswers || "Correct Answers"}
          </div>
        </div>

        <div className={`${styles.statBox} ${styles.incorrectBox}`}>
          <div className={styles.statValue}>
            <span className={styles.crossIcon}>✕</span>
            {summary?.wrong_answers ?? 0}
          </div>
          <div className={styles.statLabel}>
            {translations?.labels?.incorrectAnswers || "Unscored Answers"}
          </div>
        </div>
      </div>

      {/* Topic Information */}
      <div className={styles.topicInfo}>
        <div className={styles.topicLeft}>
          <p>
            <strong>{translations?.labels?.topic || "Topic"} :</strong>{" "}
            {assessment?.topic || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.subTopic || "Sub Topic"} :</strong>{" "}
            {assessment?.sub_topic || "-"}
          </p>
        </div>
        <div className={styles.topicRight}>
          <p>
            <strong>{translations?.labels?.subject || "Subject"} :</strong>{" "}
            {assessment?.subject || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.class || "Class"} :</strong>{" "}
            {assessment?.class_name || "-"}
            {assessment?.section_id ? ` - ${assessment.section_id}` : ""}
          </p>
        </div>
      </div>

      {/* Questions List */}
      <div className={styles.questionsList}>
        {questions?.map((question, index) => {
          const studentAnswer = question.student_answer;
          const correctAnswer = question.correct_answer;
          const isCorrect = question.is_correct === 1;

          return (
            <div key={question.id} className={styles.questionCard}>
              <p className={styles.questionText}>
                {index + 1}. {question.assessment_question}
              </p>

              <div className={styles.optionsGrid}>
                {optionLetters.map((letter) => {
                  const optionText = getOptionText(question, letter);
                  if (!optionText) return null;

                  const isStudentChoice = studentAnswer === letter;
                  const isCorrectChoice = correctAnswer === letter;

                  let optionClass = styles.option;
                  if (isStudentChoice && isCorrect) {
                    optionClass += ` ${styles.correctSelected}`;
                  } else if (isStudentChoice && !isCorrect) {
                    optionClass += ` ${styles.wrongSelected}`;
                  }

                  return (
                    <div key={letter} className={optionClass}>
                      <span
                        className={`${styles.radio} ${isStudentChoice ? styles.radioSelected : ""
                          } ${isStudentChoice && isCorrect
                            ? styles.radioCorrect
                            : ""
                          } ${isStudentChoice && !isCorrect ? styles.radioWrong : ""
                          }`}
                      ></span>
                      <span className={styles.optionLabel}>
                        {letter}) {optionText}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Answer Explanation Banner */}
              <div
                className={`${styles.explanationBanner} ${styles.correctBanner}`}
              >
                <span className={styles.bannerIcon}>
                  <FaCheck />
                </span>
                <div className={styles.bannerContent}>
                  <p className={styles.bannerAnswer}>
                    {correctAnswer}) {getOptionText(question, correctAnswer)}
                  </p>
                  <p className={styles.bannerExplanation}>
                    <strong>
                      {translations?.labels?.explanation || "Explanation"}{" "}
                      :{" "}
                    </strong>
                    {question.explanation}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer with centered Cancel button */}
      <div className={styles.footer}>
        <button
          onClick={handleBack}
          className={styles.cancelButton}
          type="button"
        >
          {translations?.labels?.cancel || "Cancel"}
        </button>
      </div>
    </div>
  );
}

export default ViewRemedialResult;
